import Glide from '@glidejs/glide';
import '@glidejs/glide/dist/css/glide.core.min.css';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import initEthosCarousels from '../util/ethosCarousel';

gsap.registerPlugin(ScrollTrigger);

function initContentCarousels() {
    document.querySelectorAll('.js-content-carousel').forEach((el) => {
        const glide = new Glide(el, {
            type: 'carousel',
            perView: 1,
            gap: 0,
            autoplay: 5000,
            hoverpause: true,
            animationDuration: 500,
        });

        const bullets = el.querySelectorAll('.glide__bullet');
        const syncBullets = () => {
            bullets.forEach((b, i) => {
                const active = i === glide.index;
                b.classList.toggle('bg-brand-forest', active);
                b.classList.toggle('bg-transparent', !active);
            });
        };

        glide.on(['mount.after', 'run'], syncBullets);
        glide.mount();
    });
}

function initTestimonialsCarousels() {
    document.querySelectorAll('.js-testimonials-carousel').forEach((el) => {
        const glide = new Glide(el, {
            type: 'carousel',
            perView: 1,
            gap: 20,
            animationDuration: 500,
        });

        // Arrows live as siblings of the glide element inside the same flex row.
        const row = el.parentElement;
        const prev = row?.querySelector('[data-prev]');
        const next = row?.querySelector('[data-next]');

        if (prev) prev.addEventListener('click', () => glide.go('<'));
        if (next) next.addEventListener('click', () => glide.go('>'));

        glide.mount();
    });
}

const STAGE_PALETTE = [
    { bg: '#272B2A', title: '#31C07F', intro: '#E5FFF4' }, // 0: graphite / emerald / dew
    { bg: '#E5FFF4', title: '#033C2D', intro: '#272B2A' }, // 1: dew / forest / graphite
    { bg: '#DEDEDE', title: '#272B2A', intro: '#272B2A' }, // 2+: ash / graphite / graphite
];

const paletteFor = (i) => STAGE_PALETTE[Math.min(i, STAGE_PALETTE.length - 1)];

function initFrameworksScrollPin() {
    document.querySelectorAll('[data-framework-stack]').forEach((section) => {
        const cards = Array.from(section.querySelectorAll('[data-stage-card]'));
        const buttons = Array.from(section.querySelectorAll('[data-stage-btn]'));
        const stack = section.querySelector('[data-stage-stack]');
        const leftPanel = section.querySelector('[data-left-panel]');
        const heading = leftPanel ? leftPanel.querySelector('h2') : null;
        const introPs = section.querySelectorAll('[data-left-intro] p');
        if (cards.length < 2 || !stack) return;

        const lastIdx = cards.length - 1;

        // Pin/scroll behaviour is desktop-only. matchMedia auto-reverts the
        // timeline, ScrollTrigger, palette tweens and inline transforms when
        // the viewport drops below lg, so md-down falls back to natural flow
        // with the dark green panel intact.
        const mm = gsap.matchMedia();

        mm.add('(min-width: 1024px)', () => {
            const getButtonHeight = () => (buttons[0] ? buttons[0].offsetHeight : 70);
            const getStackHeight = () => stack.offsetHeight;

            const headerHeight = 64; // .h-16 row inside [data-header-2026]
            const fixedOffset = () => {
                const adminBarRaw = getComputedStyle(document.body)
                    .getPropertyValue('--wp-admin-bar-height')
                    .trim();
                const adminBar = parseInt(adminBarRaw, 10) || 0;
                return headerHeight + adminBar;
            };

            const sizeCards = () => {
                const H = stack.offsetHeight;
                const B = getButtonHeight();
                const cardHeight = H - lastIdx * B;
                cards.forEach((card) => {
                    card.style.height = `${cardHeight}px`;
                });
            };

            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: () => `top top+=${fixedOffset()}`,
                    end: () => `+=${lastIdx * window.innerHeight}`,
                    pin: true,
                    pinSpacing: true,
                    anticipatePin: 1,
                    scrub: 0.5,
                    invalidateOnRefresh: true,
                    onRefresh: sizeCards,
                    snap: {
                        snapTo: 1 / lastIdx,
                        duration: { min: 0.2, max: 0.5 },
                        ease: 'power1.inOut',
                    },
                },
            });

            cards.forEach((card, i) => {
                if (i === 0) return;
                const start = (i - 1) / lastIdx;
                const dur = 1 / lastIdx;

                tl.fromTo(
                    card,
                    { y: () => getStackHeight() },
                    { y: () => i * getButtonHeight(), duration: dur, ease: 'none' },
                    start,
                );

                const prev = paletteFor(i - 1);
                const curr = paletteFor(i);

                if (leftPanel && curr.bg !== prev.bg) {
                    tl.to(leftPanel, { backgroundColor: curr.bg, duration: dur, ease: 'none' }, start);
                }
                if (heading && curr.title !== prev.title) {
                    tl.to(heading, { color: curr.title, duration: dur, ease: 'none' }, start);
                }
                if (introPs.length && curr.intro !== prev.intro) {
                    tl.to(introPs, { color: curr.intro, duration: dur, ease: 'none' }, start);
                }

                tl.to(buttons[i], { borderTopColor: 'rgba(222, 222, 222, 1)', duration: dur, ease: 'none' }, start);
            });

            const trigger = tl.scrollTrigger;
            const clickHandlers = buttons.map((btn, i) => {
                const handler = () => {
                    const target = trigger.start + (trigger.end - trigger.start) * (i / lastIdx);
                    window.scrollTo({ top: target, behavior: 'smooth' });
                };
                btn.addEventListener('click', handler);
                return handler;
            });

            let resizeRaf = 0;
            let resizeTimer = 0;
            const handleResize = () => {
                cancelAnimationFrame(resizeRaf);
                resizeRaf = requestAnimationFrame(sizeCards);
                clearTimeout(resizeTimer);
                resizeTimer = window.setTimeout(() => {
                    ScrollTrigger.refresh();
                }, 150);
            };
            window.addEventListener('resize', handleResize);
            window.addEventListener('orientationchange', handleResize);

            return () => {
                buttons.forEach((btn, i) => btn.removeEventListener('click', clickHandlers[i]));
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('orientationchange', handleResize);
                cancelAnimationFrame(resizeRaf);
                clearTimeout(resizeTimer);
                cards.forEach((card) => {
                    card.style.height = '';
                });
            };
        });
    });
}

function initClientsPartnersCarousels() {
    const TAB_CLASSES = [
        'bg-brand-forest',
        'bg-brand-emerald',
        'bg-brand-graphite',
        'bg-neutrals-100',
        'text-white',
        'text-brand-forest',
        'text-brand-emerald',
        'text-brand-graphite',
        'border-brand-forest',
        'border-brand-emerald',
    ];

    document.querySelectorAll('.js-clients-partners-carousel').forEach((section) => {
        const track = section.querySelector('[data-track]');
        const title = section.querySelector('[data-title]');
        const body = section.querySelector('[data-body]');
        const panels = Array.from(section.querySelectorAll('[data-panel]'));
        const tabs = Array.from(section.querySelectorAll('[data-tab]'));
        if (!track || panels.length < 2 || !tabs.length) return;

        const keys = panels.map((p) => p.dataset.panel);

        const activate = (key) => {
            const idx = keys.indexOf(key);
            if (idx < 0) return;

            panels.forEach((p) => {
                const isActive = p.dataset.panel === key;
                p.classList.toggle('opacity-100', isActive);
                p.classList.toggle('opacity-0', !isActive);
                p.classList.toggle('pointer-events-none', !isActive);
            });

            const isClients = idx === 0;

            section.classList.toggle('bg-neutrals-100', isClients);
            section.classList.toggle('bg-brand-graphite', !isClients);

            if (title) {
                title.classList.toggle('text-brand-forest', isClients);
                title.classList.toggle('text-brand-emerald', !isClients);
            }

            if (body) {
                body.classList.toggle('text-brand-graphite', isClients);
                body.classList.toggle('text-brand-blush', !isClients);
            }

            tabs.forEach((btn) => {
                const active = btn.dataset.tab === key;
                btn.classList.remove(...TAB_CLASSES);

                if (isClients) {
                    if (active) {
                        btn.classList.add('bg-brand-forest', 'text-white', 'border-brand-forest');
                    } else {
                        btn.classList.add('bg-neutrals-100', 'text-brand-forest', 'border-brand-forest');
                    }
                } else if (active) {
                    btn.classList.add('bg-brand-emerald', 'text-brand-graphite', 'border-brand-emerald');
                } else {
                    btn.classList.add('bg-brand-graphite', 'text-brand-emerald', 'border-brand-emerald');
                }
            });
        };

        tabs.forEach((btn) => {
            btn.addEventListener('click', () => activate(btn.dataset.tab));
        });
    });
}

export default {
    init() {
        initContentCarousels();
        initEthosCarousels();
        initTestimonialsCarousels();
        initClientsPartnersCarousels();
        initFrameworksScrollPin();
    },
    finalize() {},
};
